/**
 * @file components/audit-tasks/SchedulePlanCard.tsx
 * @description 稽核排程計劃卡片（序67/68/69）。
 *
 * 三塊內容：
 *   1. 現場稽核日程（序67；2026-09-25 序75 改版）：多組「現場稽核日期 + 當日行程」，
 *      行程列不再逐行填日期（繼承所屬組日期），每組可「載入範例行程」；與稽核計畫表單
 *      「稽核活動行程」共用同一個 DailyScheduleField 元件與資料形狀（SiteScheduleDay[]）；
 *   2. 抽樣計劃（序68）：預計抽樣件數 / 檢測項目（不檢驗須說明）/ 抽樣數量說明及評估原因概述；
 *   3. 前次不符合清單（序69）：唯讀展示該客戶歷史 NC（與 D-ZHSR02-29 表單預填同源）。
 *
 * 保存：PATCH /api/audit-tasks/:id/schedule-plan → audit_tasks.schedule_json.siteDays；
 * 舊資料 schedule_json.days（每行帶日期）讀取時以 normalizeSiteScheduleDays 依日期分組相容。
 * 序71：表單「稽核活動行程」以此為主源導入填充。
 */

import { useMemo, useState } from 'react';
import { Alert, Button, Card, Input, InputNumber, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { DailyScheduleField, type FormField } from '@zhcb/forms/browser';
import {
  getSampleSiteScheduleRows,
  normalizeSiteScheduleDays,
  siteScheduleRowHasContent,
  type AuditTaskDetail,
  type SiteScheduleDay,
} from '@zhcb/shared';
import { message } from '@/lib/antd-app';
import { apiClient } from '@/lib/api-client';

/** 抽樣計劃（序68） */
interface SamplingPlan {
  plannedCount: number | null;
  testItems: string;
  samplingNote: string;
}

/** 前次 NC 行（GET /prior-ncs 回傳項） */
interface PriorNcRow {
  code: string;
  clauseRef: string | null;
  description: string;
  severity: string;
  closedAt: string | null;
}

/**
 * 從 task.scheduleJson 解析初始值（容錯：非法形狀回空）。
 * 行程優先讀新形狀 siteDays；舊形狀 days（每行帶日期 / 起訖時間）依日期分組轉換（序75 相容）。
 */
function parseSchedule(raw: unknown): { days: SiteScheduleDay[]; sampling: SamplingPlan } {
  const fallback = {
    days: [] as SiteScheduleDay[],
    sampling: { plannedCount: null, testItems: '', samplingNote: '' } as SamplingPlan,
  };
  if (!raw || typeof raw !== 'object') return fallback;
  const obj = raw as { days?: unknown; siteDays?: unknown; sampling?: unknown };
  const days = normalizeSiteScheduleDays(
    Array.isArray(obj.siteDays) && obj.siteDays.length > 0 ? obj.siteDays : obj.days,
  );
  const s = (obj.sampling ?? {}) as Partial<SamplingPlan>;
  return {
    days,
    sampling: {
      plannedCount: typeof s.plannedCount === 'number' ? s.plannedCount : null,
      testItems: s.testItems ?? '',
      samplingNote: s.samplingNote ?? '',
    },
  };
}

interface Props {
  task: AuditTaskDetail;
  /** 唯讀（如管理組視角） */
  readOnly?: boolean;
}

export function SchedulePlanCard({ task, readOnly = false }: Props) {
  const qc = useQueryClient();
  const initial = parseSchedule(task.scheduleJson);
  const [days, setDays] = useState<unknown>(initial.days);

  /** 多日行程欄位定義（與稽核計畫表單「稽核活動行程」同欄位；範例依案件方案 TOP / TAP） */
  const scheduleField = useMemo<Extract<FormField, { type: 'dailySchedule' }>>(
    () => ({
      id: 'site_schedule',
      label: '現場稽核日程',
      type: 'dailySchedule',
      dateLabel: '現場稽核日期',
      columns: [
        { id: 'time', label: '時間', type: 'text' },
        { id: 'activity', label: '稽核活動內容或牽涉的標準條款', type: 'text' },
        { id: 'location', label: '場所、部門或人員', type: 'text' },
        { id: 'group', label: '組別', type: 'text' },
        { id: 'accompany', label: '陪同稽核人／職稱', type: 'text' },
      ],
      sampleRows: getSampleSiteScheduleRows(task.caseScheme).map((r) => ({ ...r })),
    }),
    [task.caseScheme],
  );
  const [sampling, setSampling] = useState<SamplingPlan>(initial.sampling);

  // 前次不符合清單（序69，唯讀）
  const priorNcQuery = useQuery<{ items: PriorNcRow[] }>({
    queryKey: ['audit-task-prior-ncs', task.id],
    queryFn: () => apiClient.get(`/api/audit-tasks/${task.id}/prior-ncs`),
  });
  const priorNcs = priorNcQuery.data?.items ?? [];

  const saveMutation = useMutation({
    mutationFn: () =>
      apiClient.patch(`/api/audit-tasks/${task.id}/schedule-plan`, {
        // 序75：新形狀 siteDays；丟棄全空行程列與「無日期且無行程」的空組
        siteDays: normalizeSiteScheduleDays(days).map((d) => ({
          date: d.date,
          rows: d.rows.filter(siteScheduleRowHasContent),
        })),
        sampling,
      }),
    onSuccess: () => {
      message.success('稽核排程已保存');
      void qc.invalidateQueries({ queryKey: ['audit-task-detail', task.id] });
    },
    onError: (err: Error) => {
      console.error('[SchedulePlanCard] save failed', err);
      message.error(err.message ?? '保存失敗');
    },
  });

  const ncCols: ColumnsType<PriorNcRow> = [
    { title: 'NC 編號', dataIndex: 'code', width: 130 },
    { title: '條款', dataIndex: 'clauseRef', width: 110, render: (v: string | null) => v ?? '—' },
    { title: '描述', dataIndex: 'description', ellipsis: true },
    {
      title: '等級',
      dataIndex: 'severity',
      width: 80,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: '狀態',
      dataIndex: 'closedAt',
      width: 90,
      render: (v: string | null) =>
        v ? <Tag color="green">已關閉</Tag> : <Tag color="red">未關閉</Tag>,
    },
  ];

  return (
    <Card
      size="small"
      title="稽核排程計劃"
      extra={
        !readOnly && (
          <Button
            type="primary"
            size="small"
            icon={<Save size={13} />}
            loading={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            保存排程
          </Button>
        )
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* 序67：現場稽核日程（可多天多筆） */}
        <div>
          <Typography.Text strong style={{ fontSize: 13 }}>
            現場稽核日程
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
            稽核人天數為 N 天時應有 N 天的詳細日程；每組「現場稽核日期」下的行程自動沿用該日期，
            可「載入範例行程」後再修改。稽核計畫表單「稽核活動行程」自此導入。
          </Typography.Text>
          <div style={{ marginTop: 8 }}>
            <DailyScheduleField
              field={scheduleField}
              value={days}
              onChange={setDays}
              disabled={readOnly}
            />
          </div>
        </div>

        {/* 序68：抽樣計劃 */}
        <div>
          <Typography.Text strong style={{ fontSize: 13 }}>
            抽樣計劃
          </Typography.Text>
          <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 8 }}>
            <Space>
              <span style={{ fontSize: 13 }}>預計抽樣件數：</span>
              <InputNumber
                min={0}
                precision={0}
                value={sampling.plannedCount}
                disabled={readOnly}
                onChange={(v) => setSampling((prev) => ({ ...prev, plannedCount: v ?? null }))}
                addonAfter="件"
              />
            </Space>
            <Input.TextArea
              rows={2}
              value={sampling.testItems}
              disabled={readOnly}
              maxLength={2000}
              placeholder="檢測項目（經評估若不進行檢驗，應加以說明）"
              onChange={(e) => setSampling((prev) => ({ ...prev, testItems: e.target.value }))}
            />
            <Input.TextArea
              rows={2}
              value={sampling.samplingNote}
              disabled={readOnly}
              maxLength={2000}
              placeholder="抽樣數量說明及評估原因概述"
              onChange={(e) => setSampling((prev) => ({ ...prev, samplingNote: e.target.value }))}
            />
          </Space>
        </div>

        {/* 序69：前次不符合清單（唯讀，與 D-ZHSR02-29 表單預填同源） */}
        <div>
          <Typography.Text strong style={{ fontSize: 13 }}>
            前次不符合清單
          </Typography.Text>
          {priorNcs.length === 0 ? (
            <Alert
              style={{ marginTop: 8 }}
              type="info"
              showIcon
              message="該客戶名下無歷史不符合項紀錄"
            />
          ) : (
            <Table<PriorNcRow>
              style={{ marginTop: 8 }}
              rowKey="code"
              dataSource={priorNcs}
              columns={ncCols}
              pagination={false}
              size="small"
            />
          )}
        </div>
      </Space>
    </Card>
  );
}
